import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  LayerStoreService,
  ORI_LAYER,
  STATE_LEVEL_LAYER,
} from '../../../../mapbox/services/layer-store.service';
import { LayersDataService } from '../layers-data.service';
import { UserAccessService } from '../../../../../user/access/user-access.service';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { combineLatest, debounceTime, takeUntil } from 'rxjs';
import {
  ChartDataByYear,
  FeatureStatus,
  PopulationService,
} from '../population-menu/population.service';
import { IncomeDistributionService } from '../wealth-menu/income-distribution.service';
import { HomeownershipService } from '../homeownership/homeownership.service';
import { HealthService } from '../health/health.service';
import { TargetAudienceService } from '../target-audience/target-audience.service';
import { CommuteAndMobilityService } from '../commute-and-mobility/commute-and-mobility.service';
import type { MapboxGeoJSONFeature } from 'mapbox-gl';
import { POIsFeatureStatus, PoisService } from '../pois/pois.service';
import { EducationService } from '../education/education.service';
import { SelectedCellService } from '../../../../mapbox/services/selected-cell.service';
import {
  COMMUTE_AND_MOBILITY_GROUP_TITLE,
  CRIME_GROUP_TITLE,
  EDUCATION_GROUP_TITLE,
  HEALTH_GROUP_TITLE,
  HOUSING_GROUP_TITLE,
  HWW_GROUP_TITLE,
  LayersMenuService,
  POI_GROUP_TITLE,
  POPULATION_GROUP_TITLE,
  TARGET_AUDIENCE_GROUP_TITLE,
  WEALTH_GROUP_TITLE,
} from '../layers-menu.service';
import {
  ALL_DATA_GROUP,
  CHARTS_GROUP,
  MAPS_GROUP,
  MenuGroupType,
  PLACES_GROUP,
} from '../../../menu-groups';
import { CrimeService } from '../crime/crime.service';
import { ModalService } from '../../../../../shared/services/modal.service';
import {
  hwwFeatures,
  SCHOOLS_POI,
} from '../../../../../shared/types/feature-data-type';
import { AsyncPipe } from '@angular/common';
import { IsGroupAccessiblePipe } from '../shared/is-group-accessible.pipe';
import { GroupTitleFormatterPipe } from './group-title-formatter.pipe';
import { FeatureTooltipComponent } from '../shared/feature-tooltip/feature-tooltip.component';
import { IsFeatureAccessiblePipe } from '../shared/is-feature-accessible.pipe';
import { ChartBlockComponent } from '../shared/chart-block/chart-block.component';
import { FeatureDisclaimerComponent } from '../shared/feature-disclaimer/feature-disclaimer.component';
import { Data, Layout } from 'plotly.js-dist-min';
import { LayerRowsComponent } from '../layer-rows/layer-rows.component';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import {
  CustomSlideToggleComponent,
  SlideToggleChange,
} from '../../../../../shared/components/custom-slide-toggle/custom-slide-toggle.component';
import { SubscriptionPlans } from '../../../../../user/user/user.model';
import { chartToMap } from '../pois/PoiDistribution';
import { MenuSearchService } from '../menu-search/menu-search.service';
import { openLockedFeaturePrompt } from '../shared/feature-tooltip/inacessible-features-util';
import { NearestPoiPanelComponent } from '../shared/poi-distance-block/poi-distance-block.component';

export interface MenuGroupData {
  title: string;
  featureStatus: FeatureStatus | null;
  e2eName?: string;
  disclaimer?: MenuGroupDisclaimer;
  scope?: {
    default?: FeatureScope;
    scopeDependentLayers?: string[];
  };

  tooltip?: {
    text: string;
    routerLink: string;
  };

  data: {
    mapColor: MapColorControlBlock[] | null;
    charts: ChartControlBlock[] | null;
    places: (PointsControlBlock | ChartControlBlock)[] | null;
  };
}

export type MenuGroupDisclaimer = {
  text: string;
  visibleOnLevels?: string[];
  visibleOnZoom?: {
    min?: number;
    max?: number;
  };
};

export type FeatureTooltip = {
  headerText: string;
  text: string;
} | null;

export interface MapColorControlBlock {
  featureName: string;
  featureConst: string;
  isAdminOnly?: boolean;
  filterGlobalMin: number | undefined;
  filterGlobalMax: number | undefined;
  e2eName?: string;
  disclaimer?: {
    text: string;
    visibleOnLevels?: string[];
  };
  accessibleOnLevels?: string[];
  tooltip: {
    headerText: string;
    text: string;
  } | null;
}

export interface ChartControlBlock {
  toggleTitle: string;
  chartName: string;
  // used for automated testing element navigation
  e2eName?: string;
  isAdminOnly?: boolean;
  plotlyChart: {
    data: Data;
    layout: Layout;
  } | null;
  yearVariable: string | null;
  featureConst: string;
  containsChartDataVariable: string;
  accessibleYears: number[] | null;
  chartStyle?: { [p: string]: any };
  comingSoon?: boolean;
  customText?: string;
  disclaimer?: {
    text: string;
    visibleOnLevels?: string[];
  };
  chartsByYears?: ChartDataByYear;
  tooltip: {
    headerText: string;
    text: string;
  } | null;
}

export interface PointsControlBlock {
  featureName: string;
  featureConst: string;
  e2eName?: string;
  isAdminOnly?: boolean;
  disclaimer?: {
    text: string;
    visibleOnLevels?: string[];
    visibleOnZoom?: {
      min?: number;
      max?: number;
    };
  };
  accessibleOnLevels?: string[];
  tooltip: {
    headerText: string;
    text: string;
  } | null;
}

export type LinkedToggles = Record<
  string,
  { charts: string[] | never[]; places: string[] | never[] }
>;

export type SubscriptionToLinkedToggles = {
  [plan in SubscriptionPlans]?: LinkedToggles;
} & {
  trial: LinkedToggles;
  unauthorized: LinkedToggles;
};

/**
 * What the menu asks of a group's own service. Both members are optional: the
 * places groups implement the layer toggle, the chart groups the data fill,
 * and Education implements both.
 */
interface GroupDataService {
  fillChartsData?(feature: MapboxGeoJSONFeature): void;
  setPointsLayerVisibility?(isVisible: boolean): void;
}

export enum FeatureScope {
  BY_COUNTRY = '_by_country',
  BY_STATE = '_by_state',
}
/**
 * Heavy in the profiler because ~11 instances each render a wide template over
 * ~94 feature rows on every global tick. Trimmed per pass: the accessibility
 * pipe is applied once per row rather than 2-4 times, the map-colour rows moved
 * into `app-layer-rows` (one plain row each, where they used to be a
 * `app-custom-radio` component apiece), and the template runs on the built-in
 * control flow rather than the structural directives.
 *
 * TODO:optimize the remaining win is OnPush, which is NOT safe yet - the
 * template reads two mutable values that emit nothing when they change:
 * `layerStore.currentZoom` (plain field) and the `placesToggleMap` FormControl
 * values (set from `handleLinkedToggles`). Give those an observable surface
 * first, or OnPush will silently render stale rows.
 */
@Component({
  selector: 'app-menu-group',
  templateUrl: './menu-group.component.html',
  styleUrls: [
    './menu-group.component.scss',
    '../../../../../app.component.scss',
  ],
  imports: [
    AsyncPipe,
    IconComponent,
    IsGroupAccessiblePipe,
    GroupTitleFormatterPipe,
    FeatureTooltipComponent,
    IsFeatureAccessiblePipe,
    LayerRowsComponent,
    ChartBlockComponent,
    FeatureDisclaimerComponent,
    ReactiveFormsModule,
    CustomSlideToggleComponent,
    NearestPoiPanelComponent,
  ],
})
export class MenuGroupComponent implements OnInit, OnDestroy {
  @Input() groupData!: MenuGroupData;
  @Input() form!: FormGroup;
  @Input() isPaidPlan!: boolean | null;

  @Input() activeMenuGroup!: MenuGroupType;
  @Input() isFiltersOpened!: boolean;
  @Input() isFeatureVisible = false;

  private destroy$: EventEmitter<boolean> = new EventEmitter<boolean>();

  public chartFeatureList: string[] = [];
  public placesFeatureList: string[] = [];
  public mapColorFeatureList: string[] = [];

  public selectedScope!: FeatureScope;

  /**
   * The service holding this group's cell data, or null for the groups that
   * have none of their own.
   *
   * This used to be a map of group title to *service field name*, read back
   * with `(this as any)[name]`. Eight of its twelve entries named a field that
   * does not exist on this component - three of them (`hwwService`,
   * `roadDensityService`, `physicalEnvironmentService`) name no service at
   * all - so `fillChartsData` silently did nothing for Population, Wealth,
   * Housing, Health, Target audience and Commute. Opening one of those groups
   * with a cell already selected left its charts under "click a cell on the
   * map", and changing a year in them fetched nothing.
   */
  private groupService: GroupDataService | null = null;

  constructor(
    public layerStore: LayerStoreService,
    private populationService: PopulationService,
    private incomeDistributionService: IncomeDistributionService,
    private homeownershipService: HomeownershipService,
    private healthService: HealthService,
    private targetAudienceService: TargetAudienceService,
    private commuteAndMobilityService: CommuteAndMobilityService,
    private layersMenuService: LayersMenuService,
    public layerDataService: LayersDataService,
    public accessService: UserAccessService,
    private selectedCellService: SelectedCellService,
    private educationService: EducationService,
    private POIsService: PoisService,
    private crimeService: CrimeService,
    private modalService: ModalService,
    private searchService: MenuSearchService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.handleSubscriptions();
    this.handleInputData();

    this.isFeatureVisible = this.layerDataService.getIsGroupOpened(
      this.groupData.title
    );
  }

  ngOnDestroy() {
    this.destroy$.emit(true);
    this.destroy$.complete();
  }

  public onActiveLayerToggle(layer: string): void {
    this.form.get('activeLayer')!.setValue(layer);

    if (this.layerStore.activeLayer.value !== layer) {
      this.layerDataService.handleOpenedGroup(this.groupData.title, true);
      this.layerStore.handleLayerChange(layer);
    }
  }

  public handleInaccessibleFeatureClick(
    feature: string,
    featureName: string
  ): void {
    if (!this.accessService.userHasAccess(feature)) {
      openLockedFeaturePrompt(
        feature,
        featureName,
        this.accessService,
        this.modalService
      );
    }
  }

  public handleSubscriptions(): void {
    this.accessService.isUserPlanUpdated
      .pipe(takeUntil(this.destroy$))
      .subscribe((updated) => {
        if (updated) {
          this.layersMenuService.reassignTooltipText(this.groupData);

          // Force change detection to rerender menu as it could stuck after login/logout
          this.cdr.detectChanges();
        }
      });

    this.layerStore.activeLayer
      .pipe(debounceTime(200), takeUntil(this.destroy$))
      .subscribe((activeLayer) => {
        // Trigger linked features on layer change and on init
        this.handleLinkedToggles(activeLayer);

        this.isFeatureVisible = this.layerDataService.getIsGroupOpened(
          this.groupData.title
        );

        if (this.groupData.scope) {
          this.selectedScope =
            (this.layerStore.activeLayer.value.endsWith(FeatureScope.BY_STATE)
              ? FeatureScope.BY_STATE
              : FeatureScope.BY_COUNTRY) ?? this.groupData.scope.default!;
        }

        const isFeatureActive =
          this.mapColorFeatureList.includes(activeLayer) ||
          (hwwFeatures.includes(activeLayer) &&
            this.groupData.title === HWW_GROUP_TITLE);

        if (isFeatureActive) {
          this.layerDataService.handleOpenedGroup(this.groupData.title, true);
          this.isFeatureVisible = true;
        }
      });

    if (this.groupData.scope) {
      combineLatest([this.layerStore.activeLayer, this.layerStore.activeLevel])
        .pipe(takeUntil(this.destroy$))
        .subscribe(([layer, level]) => {
          if (
            level === STATE_LEVEL_LAYER &&
            this.selectedScope !== FeatureScope.BY_COUNTRY
          ) {
            this.onScopeToggle(FeatureScope.BY_COUNTRY);
          }
        });
    }

    // Update isFeatureVisible depending on menu search query
    this.searchService.searchQuery$
      .pipe(takeUntil(this.destroy$), debounceTime(50))
      .subscribe(([_, query]) => {
        if (query) {
          this.isFeatureVisible = !!query.trim();
          this.layerDataService.handleOpenedGroup(
            this.groupData.title,
            this.isFeatureVisible
          );
        }
      });
  }

  private handleLinkedToggles(layer: string): void {
    const currentPlan = this.accessService.getUserPlan();
    const isTrial = this.accessService.getIsTrial();
    const isUnauthorized = this.accessService.getIsUnauthorized();

    const linkedToggles = currentPlan
      ? this.layerDataService.linkedToggles[currentPlan]
      : isTrial
        ? this.layerDataService.linkedToggles.trial
        : this.layerDataService.linkedToggles.unauthorized;

    if (hwwFeatures.includes(layer)) {
      layer = layer.split('_by_')[0];
    }

    if (!linkedToggles || !linkedToggles[layer]) {
      return;
    }

    if (linkedToggles[layer].places && linkedToggles[layer].places.length > 0) {
      linkedToggles[layer].places.forEach((place) => {
        const control = this.layerDataService.placesToggleMap.get(place);
        if (control) {
          control.setValue(true);

          const groupTitle =
            this.layerDataService.getGroupNameByFeatureConst(place);

          this.layerDataService.handleOpenedGroup(groupTitle, true);

          switch (place) {
            case SCHOOLS_POI:
              this.educationService.setPointsLayerVisibility(true);
              break;
            case ORI_LAYER:
              this.crimeService.setPointsLayerVisibility(true);
              break;
          }
        }
      });
    }

    if (linkedToggles[layer].charts && linkedToggles[layer].charts.length > 0) {
      linkedToggles[layer].charts.forEach((chart) => {
        const groupTitle =
          this.layerDataService.getGroupNameByFeatureConst(chart);

        this.layerDataService.handleOpenedGroup(groupTitle, true);

        if (this.layerDataService.toggleMap.has(chart)) {
          this.layerDataService.toggleMap.get(chart)?.setValue(true);
        }
      });
    }
  }

  private updateActivePOITogglesArray(toggles: { [key: string]: boolean }) {
    const poiStatus = this.groupData.featureStatus as POIsFeatureStatus;
    if (!poiStatus || !poiStatus.activePoisCharts) {
      return;
    }

    Object.entries(toggles).forEach(([key, value]) => {
      // Only process valid POI fetched from /pois
      if (!chartToMap.has(key)) {
        return;
      }

      const activeCharts = (this.groupData.featureStatus! as POIsFeatureStatus)
        .activePoisCharts;
      if (value && !activeCharts!.includes(key)) {
        activeCharts!.push(key);
      } else if (!value) {
        const index = activeCharts!.indexOf(key);
        if (index != -1) {
          activeCharts!.splice(index, 1);
        }
      }
    });

    this.POIsService.handlePoisOnMap();
    this.POIsService.handlePoiLayerVisibility();
  }

  // Age/sex and race/ethnicity each arrive as one response holding every
  // year at once (see PopulationService.chartsByYears), so switching between
  // their years is a local read. Refetching here would still "work", but the
  // response lands after the visitor has already moved on and pins
  // featureStatus back to its own (latest) year, undoing their pick.
  private static readonly PRELOADED_YEAR_VARIABLES = new Set([
    'ageSexYear',
    'raceDistributionYear',
  ]);

  public onSelectedYearChange(data: { year: number; usedFor: string }): void {
    // @ts-ignore
    this.groupData.featureStatus[data.usedFor] = data.year;

    if (!MenuGroupComponent.PRELOADED_YEAR_VARIABLES.has(data.usedFor)) {
      this.handleFillChartsData();
    }
  }

  public toggleGroup(): void {
    this.isFeatureVisible = !this.isFeatureVisible;
    this.layerDataService.handleOpenedGroup(
      this.groupData.title,
      this.isFeatureVisible
    );

    // Reopening an accordion is not new information: without the staleness
    // check every collapse and expand refires the group's whole fetch (five
    // requests for Housing) for the cell it is already holding, and flashes
    // the reload overlay across charts that are already drawn.
    if (this.isFeatureVisible) {
      this.fillChartsDataIfStale();
    }
  }

  private openDefaultGroup(): void {
    this.layerDataService.handleOpenedGroup(
      this.groupData.title,
      this.mapColorFeatureList.includes(this.layerStore.activeLayer.value)
    );
    this.isFeatureVisible = this.layerDataService.getIsGroupOpened(
      this.groupData.title
    );
  }

  private handleInputData(): void {
    this.groupService = this.serviceForGroup(this.groupData.title);

    if (this.groupData.data.charts) {
      this.groupData.data.charts.forEach((chart) => {
        const control = new FormControl(false);
        this.layerDataService.toggleMap.set(chart.featureConst, control);

        // Switching a chart on is the other way into a group's data. The cell
        // click only fills groups that already had something open (see
        // LayersDataService.handleClick), so without this a chart opened after
        // the click sat under "click a cell on the map" with the cell clicked.
        control.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe((isOn) => {
            if (isOn) this.fillChartsDataIfStale();
          });
      });

      this.chartFeatureList = Object.values(this.groupData.data.charts).map(
        ({ featureConst }) => featureConst
      );
    }

    if (this.groupData.data.places) {
      this.groupData.data.places.forEach((place) => {
        this.layerDataService.placesToggleMap.set(
          place.featureConst,
          new FormControl(false)
        );
      });

      this.layerDataService.placesToggleMap.forEach((control, key) => {
        if (control) {
          // The controls live in LayerDataService (root), so they outlive this
          // component - without takeUntil each menu re-render stacks another
          // handler on the same shared control.
          control.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((value) => {
              this.updateActivePOITogglesArray({ [key]: value });

              // Same gap as the chart toggles above, for the places that draw
              // a chart of their own (the POI distributions). A place that
              // only paints points on the map needs no cell data, so it does
              // not pay for a fetch.
              if (value && this.hasOwnChart(key)) this.fillChartsDataIfStale();
            });
        }
      });

      this.placesFeatureList = Object.values(this.groupData.data.places).map(
        ({ featureConst }) => featureConst
      );
    }

    if (this.groupData.data.mapColor) {
      this.mapColorFeatureList = Object.values(
        this.groupData.data.mapColor
      ).map(({ featureConst }) => featureConst);
    }

    this.openDefaultGroup();
  }

  /**
   * Fetches only when this group is not already holding the selected cell's
   * data: opening a second chart in a group must not throw the first one back
   * into a loading placeholder. A year change goes through
   * `handleFillChartsData` directly - same cell, but the data really has
   * changed.
   */
  private fillChartsDataIfStale(): void {
    const status = this.groupData.featureStatus;

    if (status?.isLoading) return;
    if (
      status?.isSelected &&
      status.naturalId === this.selectedCellService.getSelectedCellGeoId()
    ) {
      return;
    }

    this.handleFillChartsData();
  }

  /** A place row that renders a chart block rather than a bare layer toggle. */
  private hasOwnChart(featureConst: string): boolean {
    return !!this.groupData.data.places?.some(
      (place) =>
        place.featureConst === featureConst &&
        !!(place as ChartControlBlock).toggleTitle
    );
  }

  private serviceForGroup(title: string): GroupDataService | null {
    switch (title) {
      case POPULATION_GROUP_TITLE:
        return this.populationService;
      case WEALTH_GROUP_TITLE:
        return this.incomeDistributionService;
      case HOUSING_GROUP_TITLE:
        return this.homeownershipService;
      case HEALTH_GROUP_TITLE:
        return this.healthService;
      case TARGET_AUDIENCE_GROUP_TITLE:
        return this.targetAudienceService;
      case COMMUTE_AND_MOBILITY_GROUP_TITLE:
        return this.commuteAndMobilityService;
      case EDUCATION_GROUP_TITLE:
        return this.educationService;
      case POI_GROUP_TITLE:
        return this.POIsService;
      case CRIME_GROUP_TITLE:
        return this.crimeService;
      // HWW, Road density and Physical environment carry no per-cell charts of
      // their own: their layers are painted from the map's bulk data.
      default:
        return null;
    }
  }

  private handleFillChartsData(): void {
    if (!this.selectedCellService.isCellSelected()) return;

    this.groupService?.fillChartsData?.(
      this.selectedCellService.getSelectedFeature() as MapboxGeoJSONFeature
    );
  }

  public onScopeToggle(scope: FeatureScope): void {
    const acceptableLayers = this.groupData.scope!.scopeDependentLayers || [];

    this.selectedScope = scope;

    const currentLayer = this.layerStore.activeLayer.value;

    if (acceptableLayers.some((layer) => currentLayer!.startsWith(layer))) {
      const baseLayer = acceptableLayers.find((layer) =>
        currentLayer!.startsWith(layer)
      );
      const newActiveLayer = baseLayer + this.selectedScope;

      this.form.get('activeLayer')!.setValue(newActiveLayer);

      this.layerStore.handleLayerChange(newActiveLayer);
    }
  }

  public handlePointsToggleChange(event: SlideToggleChange): void {
    this.groupService?.setPointsLayerVisibility?.(event.checked);
  }

  public handleTooltipModalOpen(modalData: any): void {
    this.modalService.openModal(modalData);
  }

  protected readonly STATE_LEVEL_LAYER = STATE_LEVEL_LAYER;
  protected readonly MAPS_GROUP = MAPS_GROUP;
  protected readonly CHARTS_GROUP = CHARTS_GROUP;
  protected readonly PLACES_GROUP = PLACES_GROUP;
  protected readonly ALL_DATA_GROUP = ALL_DATA_GROUP;
  protected readonly POI_GROUP_TITLE = POI_GROUP_TITLE;
  protected readonly EDUCATION_GROUP_TITLE = EDUCATION_GROUP_TITLE;
  protected readonly CRIME_GROUP_TITLE = CRIME_GROUP_TITLE;
  protected readonly SCHOOLS_POI = SCHOOLS_POI;
  protected readonly ORI_LAYER = ORI_LAYER;
}
